import { defineComponent as _defineComponent } from 'vue'
export interface TableContext {
  filter: string | null;
}

export interface ProviderResults {
  items: DataItem[];
}

export type DataProvider = (ctx: TableContext) => Promise<ProviderResults>;

/**
 * `TableProgressive` component properties.
 */
export interface Props {
  table: TablePanels.ModuleKey;
  pk: string;
  label: string;
  columns: Columns;
  operations?: Operations;
  createAction?: string;
  deleteAction?: string;
  loading?: boolean;
  tableQueryText?: string;
  filterFields?: string[];
  specialFilters?: TP.SpecialFilter<DataItem>[];
  filterTags?: TP.FilterTag[];
  filterParams?: TP.FilterParam[];
  infoMessage?: string | null;
}


import { computed, watch } from 'vue';
import { TablePanel as TP } from 'table-panel';
import { toString } from '@utils/to-string';
import { dates } from '@utils/dates';

import { TablePanels } from '@store';
import { delay } from '@tools/delay';
import { isString } from '@tools/type-guards';

import { useFlyout } from './flyout';
import { useTable, Columns, Operations, DataItem } from './table';
import { useProgressiveLoader } from './progressive-loader';
import CellOperations from './CellOperations.vue';
import TableOverlay from './TableOverlay.vue';
import Toolbar from './Toolbar.vue';

const __default__ = { name: 'TableProgressive' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    table: null,
    pk: null,
    label: null,
    columns: null,
    operations: { default: () => [] },
    createAction: { default: '' },
    deleteAction: { default: '' },
    loading: { type: Boolean },
    tableQueryText: { default: '' },
    filterFields: { default: () => [] },
    specialFilters: { default: () => [] },
    filterTags: { default: () => [] },
    filterParams: { default: () => [] },
    infoMessage: { default: null }
  },
  setup(__props: any) {

const props = __props as { table: TablePanels.ModuleKey, pk: string, label: string, columns: Columns, operations: Operations, createAction: string, deleteAction: string, loading?: boolean, tableQueryText: string, filterFields: string[], specialFilters: TP.SpecialFilter<DataItem>[], filterTags: TP.FilterTag[], filterParams: TP.FilterParam[], infoMessage: string | null };



const {
  queryText,
  maximized,
  tableFilters,
  fullscreen,
  items,
  displayedItems,
  page,
  allRowsSelected,
  customCells,
  operations,
  tableFilterTags,
  tableFilterParams,
  tableLoading,
  ...table
} = useTable(props);

const {
  loadingPage,
  loadProgress,
  loadDeferred,
  loadItems,
  onScroll,
  onAnimationEnd,
} = useProgressiveLoader({
  tableKey: props.table,
  tableFilterTags,
  tableFilterParams,
});

const flyout = useFlyout(operations.value ?? []);

const rowCount = computed(() => items.value.length);

const classList = computed(() => {
  const items: string[] = ['table-panel', 'progressive-table'];

  if (maximized.value) {
    items.push('fullscreen');

    if (tableFilters.value.length) {
      items.push('filter-offset');
    }
  }

  return items;
});

const fields = computed(() => {
  return table.fields.value.map((field) => {
    const value = isString(field) ? { key: field } : { ...field };

    return { ...value, sortable: false };
  });
});

const tableOverlay = computed(() => {
  let text: string;
  let transparent = false;

  if (loadingPage.value) {
    text = 'Loading';
    transparent = true;
  } else if (props.loading || tableLoading.value) {
    text = 'Loading';
  } else if (!rowCount.value) {
    text = 'No Items';
    transparent = true;
  } else {
    return null;
  }

  return { text, transparent };
});

watch(
  () => props.tableQueryText,
  () => {
    void loadItems(true);
  },
);

watch([tableFilterTags, tableFilterParams], async () => {
  if (loadingPage.value) {
    await loadDeferred.value?.promise;

    await delay();
  }

  void loadItems(true);
});

function openFlyout(item: DataItem, _index: number, event: MouseEvent) {
  event.preventDefault();

  flyout.open(item, event.x, event.y);
}

function sortCompare(
  aRow: Record<string, unknown>,
  bRow: Record<string, unknown>,
  key: string,
  sortDesc: boolean,
  compareOptions: unknown,
  compareLocale: unknown,
) {
  /**
   * Don't forget this function's sister function under TableRegular.vue!
   * Also, not sure if this is ever used in a progressive loader table
   */

  /**
   * sortDesc should not be necessary since b-table will handle the direction
   * according to Bootstrap's documentation
   */

  // try to find column and detect if date or dateTime type is given
  const column = props.columns.find((col) => col.key === key);

  let a;
  let b;

  /**
   *  if the column's value is created with a function, we need to call that
   * function to get the proper value. Otherwise we can take the raw value
   * from the row[key]
   */
  if (typeof column?.value === 'function') {
    a = evokeSafely(() => (column.value as GenericFunction)(aRow));
    b = evokeSafely(() => (column.value as GenericFunction)(bRow));
  } else {
    a = aRow[key];
    b = bRow[key];
  }

  // we need to sort dates differently than raw sort
  if (column && (column.type === 'date' || column.type === 'dateTime')) {
    let dateA: DateLike;
    let dateB: DateLike;

    try {
      dateA = dates.toDate(a as string);
      dateB = dates.toDate(b as string);
    } catch {
      return 0;
    }

    if (dates.isBefore(dateA, dateB)) return 1;

    if (dates.isAfter(dateA, dateB)) return -1;

    return 0;
  } else if (column && column.type === 'currency') {
    // we need to sort currency differently than raw sort

    let valueA: number;
    let valueB: number;

    // we could be given a variety of "currency" type with any
    // number of decimals, commas, and denomination symbols
    // so we'll want to strip all of that out to a raw number
    if (typeof a === 'string') {
      valueA = parseFloat(a.replace(/[^0-9.-]+/g, ''));
    } else if (typeof a === 'number') {
      valueA = a;
    } else {
      return 0;
    }

    if (typeof b === 'string') {
      valueB = parseFloat(b.replace(/[^0-9.-]+/g, ''));
    } else if (typeof b === 'number') {
      valueB = b;
    } else {
      return 0;
    }

    return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
  } else if (typeof a === 'number' && typeof b === 'number') {
    // If both compared fields are native numbers or both are native dates
    return a < b ? -1 : a > b ? 1 : 0;
  } else {
    return toString(a).localeCompare(
      toString(b),
      compareLocale,
      compareOptions,
    );
  }
}

function evokeSafely(fn: () => unknown) {
  let value: unknown;

  try {
    value = fn();
  } catch {
    // Ignore any errors.
  }

  return value;
}

return { __sfc: true,props, queryText, maximized, tableFilters, fullscreen, items, displayedItems, page, allRowsSelected, customCells, operations, tableFilterTags, tableFilterParams, tableLoading, table, loadingPage, loadProgress, loadDeferred, loadItems, onScroll, onAnimationEnd, flyout, rowCount, classList, fields, tableOverlay, openFlyout, sortCompare, evokeSafely, CellOperations, TableOverlay, Toolbar }
}

})